/*
    The insights payload as the backend sends it, field for field.

    Mirrored here rather than shared with the booking drawer's own local interfaces
    (shared/performance-insights) because that panel reads a subset — it predates the per-channel
    and per-audience frequency lists, and widening its interfaces would make it null-check fields it
    never renders. The server-side shape is CampaignPerformanceInsightsDTO.
*/

/** One line of a performance table — a channel, a city, or the campaign total. */
export interface PerformanceInsightRow {
    label: string;
    /** Set on a channel row, null on a city or total row. */
    channelId: number;
    impressions: number;
    attentiveImpressions: number;
    grp: number;
    attentiveGrp: number;
    /** Null where the provider cannot break reach down this far — rendered as 0, per the ruling below. */
    reach: number;
    population: number;
}

/** How much of a population saw the campaign at least {@link frequency} times. */
export interface FrequencyDistributionRow {
    /** "1+", "2+", "3+"… as the provider labels it. */
    frequency: string;
    /** Share of that population, 0-100. */
    percentOfAudience: number;
    audience: number;
}

/** One slice of an audience — a gender, an age band or an income bracket. */
export interface AudienceBreakdownRow {
    label: string;
    reach: number;
    percentOfAudience: number;
    impressions: number;
    attentiveImpressions: number;
}

/** The selected channel's own audience insights. */
export interface ChannelAudienceInsights {
    channelId: number;
    label: string;
    frequencyDistribution: FrequencyDistributionRow[];
    genderDistribution: AudienceBreakdownRow[];
    ageDistribution: AudienceBreakdownRow[];
    incomeDistribution: AudienceBreakdownRow[];
}

/** One channel's frequency curve. Every channel in the list shares one set of frequencies. */
export interface ChannelFrequency {
    channelId: number;
    label: string;
    frequencyDistribution: FrequencyDistributionRow[];
}

/** What one channel, or one city, did on one date. Carries no reach or GRP: neither divides by day. */
export interface PerformanceTrendPoint {
    /** ISO date. */
    date: string;
    label: string;
    channelId: number;
    impressions: number;
    attentiveImpressions: number;
}

export interface CampaignPerformanceInsights {
    available: boolean;
    attentiveAvailable: boolean;
    /** True for measured figures, false for a forecast. Decides how the page labels its zeroes. */
    actual: boolean;
    byChannel: PerformanceInsightRow[];
    byCity: PerformanceInsightRow[];
    frequencyDistribution: FrequencyDistributionRow[];
    frequencyByChannel: ChannelFrequency[];
    selectedChannel: ChannelAudienceInsights;
    /**
     * Every booked channel's audience insights, so the channel dropdown and the PDF export can
     * switch between them without a round trip. Empty on the forecast path, where a channel's
     * audience is a separate provider call — callers fall back to `selectedChannel` there.
     */
    channelAudiences: ChannelAudienceInsights[];
    perChannelPerDay: PerformanceTrendPoint[];
    perCityPerDay: PerformanceTrendPoint[];
    total: PerformanceInsightRow;
}

/** The metric the audience and over-time sections are read in. */
export type InsightsMetric = 'OTS' | 'LTS' | 'Reach';

/** How the over-time series is bucketed. */
export type TrendGranularity = 'Day' | 'Week' | 'Month';

export const ZERO_ROW: PerformanceInsightRow = {
    label: '',
    channelId: null,
    impressions: 0,
    attentiveImpressions: 0,
    grp: 0,
    attentiveGrp: 0,
    reach: 0,
    population: 0
};

/**
 * What the page renders when the call fails or the provider answers with nothing.
 *
 * Zeroes rather than null throughout, so every panel still draws its axes, its headings and its
 * totals row. The client ruled on this directly: a zero is wanted over a panel that disappears.
 * `available` carries the distinction for anything that needs to word itself honestly — a zero here
 * means "not reported", not "nobody saw it".
 */
export function emptyInsights(): CampaignPerformanceInsights {
    return {
        available: false,
        attentiveAvailable: false,
        actual: false,
        byChannel: [],
        byCity: [],
        frequencyDistribution: [],
        frequencyByChannel: [],
        selectedChannel: {
            channelId: null,
            label: '',
            frequencyDistribution: [],
            genderDistribution: [],
            ageDistribution: [],
            incomeDistribution: []
        },
        channelAudiences: [],
        perChannelPerDay: [],
        perCityPerDay: [],
        total: { ...ZERO_ROW, label: 'Total' }
    };
}
